import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete,
    Box,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import Swal from 'sweetalert2';
import { EmailTemplateBuilderServiceQuery } from '../../../services/query';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import './SectionGallery.scss';
import { SCUIButton } from '../../../atomic/atoms';
import { bindTrigger, bindMenu } from 'material-ui-popup-state/hooks';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';

import PopupState from 'material-ui-popup-state';
import EmailTemplateBuilderCommand from '../../../services/command/emailTemplateBuilder/EmailTemplateBuilderCommand.services';
import { sectionTypes } from '../EmailTemplateBuilder.config';
import { LocalizationManagementQuery, MasterDataQuery } from '../../../services/query';
import { ETBSG, etbCls } from '../emailTemplateBuilder.classes';

export const SectionGallery: FC<any> = () => {
    const { t } = useTranslation();
    const sectionTypeList = sectionTypes;
    const [filteredDatas, setFilteredDatas] = React.useState<any>();
    const [allRowCount, setAllRowCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [filteredCountryList, setFilteredCountryList] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState('TR');
    const [selectedType, setSelectedType] = useState<number>(2);
    const [languageList, setLanguageList] = useState<any>();
    const [selectedLanguage, setSelectedLanguage] = useState('tr-TR');

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    const GetCountryList = async () => {
        MasterDataQuery.GetCountryList().then((res: any) => {
            let parseData = res.data.map((item: any) => {
                return {
                    id: item.countryId,
                    key: item.countryKey,
                    name: item.countryCode,
                    dropdownValueText: item.translatedCountryName,
                };
            });
            setFilteredCountryList(parseData);
        });
    };

    const GetLanguageList = async () => {
        LocalizationManagementQuery.CultureList().then((res: any) => {
            setLanguageList(res.data);
        });
    };

    useEffect(() => {
        GetCountryList();
        GetLanguageList();
        getSections();
    }, []);

    useEffect(() => {
        if (currentPage > 1) {
            getSections();
        }
    }, [currentPage, selectedCountry, selectedLanguage]);

    const handleDeleteClick = async (sectionId: any) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: t('emailTemplateBuilder.deleteSectionTitle'),
            text: t('emailTemplateBuilder.deleteSectionConfirmation'),
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.yes'),
            cancelButtonText: t('general.no'),
        });

        if (!result.isConfirmed) {
            return;
        }

        const req = { sectionId: sectionId };
        await EmailTemplateBuilderCommand.DeleteSection(req);
        await Swal.fire({
            icon: 'success',
            title: t('general.deleted'),
            text: t('emailTemplateBuilder.sectionDeleted'),
        });
        getSections();
    };
    const handleClick = (popupState: any, tur: string, sectionId: any) => {
        popupState.close();
        switch (tur) {
            case 'edit': {
                window.open('/emailTemplateBuilder/editSection/' + sectionId, '_self');

                break;
            }
            case 'delete': {
                handleDeleteClick(sectionId);
                break;
            }
            default: {
                break;
            }
        }
    };
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getSections({
                type: selectedType,
                keyword: searchKeyword,
                languageCode: selectedLanguage,
                countryCode: selectedCountry,
                take: 10,
                skip: 1,
            });
            setCurrentPage(1);
        }
    };

    const handleFilter = () => {
        getSections({
            type: selectedType,
            keyword: searchKeyword,
            languageCode: selectedLanguage,
            countryCode: selectedCountry,
            take: 10,
            skip: 1,
        });
        setCurrentPage(1);
    };

    const getSections = async (req?: any) => {
        if (!req) {
            const take = 10;
            req = {
                type: selectedType,
                keyword: searchKeyword,
                languageCode: selectedLanguage,
                countryCode: selectedCountry,
                take: take,
                skip: currentPage,
            };
        }
        EmailTemplateBuilderServiceQuery.GetSectionData(req).then((res: any) => {
            if (res.data && Array.isArray(res.data) && res.data.length > 0) {
                let parseData = res.data
                    .map((item: any, index: any) => {
                        return {
                            id: index,
                            sectionId: item.sectionId,
                            name: item.name || '-',
                            key: item.key || '-',
                            specialCode: item.specialCode || '-',
                            content: item.content || '-',
                            languageCode: item.languageCode || '-',
                            countryCode: item.countryCode || '-',
                            createOn: item.createOn || '-',
                            sections: item.sections,
                        };
                    })
                    .flat();
                setAllRowCount(res.totalPages);
                setFilteredDatas(parseData);
            } else {
                setAllRowCount(0);
                setFilteredDatas([]);
                Swal.fire({
                    icon: 'info',
                    title: t('general.noDataFound'),
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
        });
    };
    return (
        <SCUIContent>
            <Box className={ETBSG.page}>
            <SCUIBreadCrumb
                dontShowHome={true}
                homeIconLink={'/home'}
                onlyTitle={'Email Template Builder'}
                components={[
                    {
                        title: t('emailTemplateBuilder.templateBuilder'),
                        link: '/emailTemplateBuilder',
                    },
                    {
                        title: t('emailTemplateBuilder.sections'),
                        link: '/emailTemplateBuilder/sectionGallery',
                    },
                ]}
            />
            <div className={etbCls('lineWrapper', ETBSG.divider)} />
            <Grid container spacing={2} alignItems="center" className={ETBSG.toolbarGrid} sx={{ mb: 2 }}>
                <Grid
                    className={ETBSG.actionsPrimary}
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    size={{
                        xs: 12,
                    }}
                >
                    <SCUIButton
                        className={ETBSG.inputButtonCreateSection}
                        text={t('emailTemplateBuilder.createNewSection')}
                        type="primary"
                        sizeType="default"
                        size={'s'}
                        href={'/emailTemplateBuilder/addSection'}
                        showStartIcon
                        startIcon={<AddIcon style={{ color: 'white', marginRight: '8px' }} />}
                        style={{ whiteSpace: 'nowrap', margin: '16px 0' }}
                    />
                </Grid>

                <Grid
                    className={ETBSG.filtersRow}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={3}
                    size={{
                        xs: 12,
                    }}
                >
                    <Box className={ETBSG.fieldCountry}>
                    <Autocomplete
                        className={ETBSG.autocompleteCountryRoot}
                        options={filteredCountryList || []}
                        getOptionLabel={(option: any) => option?.dropdownValueText || ''}
                        value={filteredCountryList?.find((item: any) => item?.name === selectedCountry) || null}
                        onChange={(_, newValue) => {
                            setSelectedCountry(newValue?.name || '');
                        }}
                        slotProps={{
                            popper: {
                                sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                className={ETBSG.inputAutocompleteCountry}
                                label={t('emailTemplateBuilder.selectCountryName')}
                            />
                        )}
                        disablePortal
                        sx={{
                            width: '300px',
                            '& .MuiOutlinedInput-root': {
                                height: '40px !important',
                                minHeight: '40px !important',
                                maxHeight: '40px !important',
                                '&.Mui-focused, &.Mui-selected, &:hover': {
                                    height: '40px !important',
                                    minHeight: '40px !important',
                                    maxHeight: '40px !important',
                                },
                            },
                            '& .MuiInputBase-input': {
                                height: '8px !important',
                                minHeight: '8px !important',
                                padding: '8px 14px !important',
                            },
                        }}
                    />
                    </Box>

                    {languageList && languageList.length > 0 && (
                        <Box className={ETBSG.fieldLanguage}>
                        <Autocomplete
                            className={ETBSG.autocompleteLanguageRoot}
                            options={languageList}
                            getOptionLabel={(option: any) => option.code}
                            value={languageList.find((item: any) => item.code === selectedLanguage) || null}
                            onChange={(_, newValue) => {
                                setSelectedLanguage(newValue?.code || 'tr-TR');
                            }}
                            slotProps={{
                                popper: {
                                    sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    className={ETBSG.inputAutocompleteLanguage}
                                    label={t('emailTemplateBuilder.selectLanguage')}
                                />
                            )}
                            disablePortal
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    height: '40px !important',
                                    minHeight: '40px !important',
                                    maxHeight: '40px !important',
                                    '&.Mui-focused, &.Mui-selected, &:hover': {
                                        height: '40px !important',
                                        minHeight: '40px !important',
                                        maxHeight: '40px !important',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    height: '8px !important',
                                    minHeight: '8px !important',
                                    padding: '8px 14px !important',
                                },
                            }}
                        />
                        </Box>
                    )}

                    {sectionTypeList && sectionTypeList.length > 0 && (
                        <Box className={ETBSG.fieldSectionType}>
                        <Autocomplete
                            className={ETBSG.autocompleteSectionTypeRoot}
                            options={sectionTypeList}
                            getOptionLabel={(option: any) => option.name}
                            value={sectionTypeList.find((item: any) => item.id === selectedType) || null}
                            onChange={(_, newValue) => {
                                setSelectedType(newValue ? newValue.id : 0);
                            }}
                            slotProps={{
                                popper: {
                                    sx: { zIndex: (theme) => theme.zIndex.modal + 1 },
                                },
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    className={ETBSG.inputAutocompleteSectionType}
                                    label={t('emailTemplateBuilder.sectionType')}
                                />
                            )}
                            disablePortal
                            sx={{
                                width: '300px',
                                '& .MuiOutlinedInput-root': {
                                    height: '40px !important',
                                    minHeight: '40px !important',
                                    maxHeight: '40px !important',
                                    '&.Mui-focused, &.Mui-selected, &:hover': {
                                        height: '40px !important',
                                        minHeight: '40px !important',
                                        maxHeight: '40px !important',
                                    },
                                },
                                '& .MuiInputBase-input': {
                                    height: '8px !important',
                                    minHeight: '8px !important',
                                    padding: '8px 14px !important',
                                },
                            }}
                        />
                        </Box>
                    )}

                    <TextField
                        className={ETBSG.inputTextfieldSearchKeyword}
                        placeholder={t('general.search')}
                        onChange={handleSearch}
                        onKeyDown={handleKeyPress}
                        size="small"
                        sx={{
                            width: '300px',
                            '& .MuiInputBase-root': {
                                height: '40px',
                            },
                        }}
                    />
                    <SCUIButton
                        className={ETBSG.inputButtonFilter}
                        text={t('general.filter')}
                        type="primary"
                        sizeType="fixed"
                        size={'s'}
                        onClick={handleFilter}
                        style={{ marginLeft: 'auto' }}
                    />
                </Grid>
            </Grid>
            <TableContainer className={ETBSG.tableContainer}>
                <Table component={Paper} className={ETBSG.table} style={{ marginBottom: '2rem' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ textAlign: 'center', minWidth: 250 }}>
                                {t('emailTemplateBuilder.sectionName')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 200 }}>
                                {t('emailTemplateBuilder.sectionKeywords')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 100 }}>
                                {t('emailTemplateBuilder.countryCode')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 100 }}>
                                {t('emailTemplateBuilder.languageCode')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 100 }}>
                                {t('emailTemplateBuilder.sectionType')}
                            </TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 80 }}>{''}</TableCell>
                            <TableCell style={{ textAlign: 'center', minWidth: 20 }}>{''}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDatas &&
                            filteredDatas.map((emailTemplate: any) => (
                                <TableRow key={emailTemplate.id} className={ETBSG.tableRow}>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.name}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.key}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.countryCode}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.languageCode}</TableCell>
                                    <TableCell style={{ textAlign: 'center' }}>{emailTemplate.type}</TableCell>

                                    <TableCell>
                                        <PopupState variant="popover" popupId="demo-popup-menu">
                                            {(popupState: any) => (
                                                <React.Fragment>
                                                    <IconButton className={ETBSG.inputIconRowMenu} {...bindTrigger(popupState)}>
                                                        {' '}
                                                        <MoreVertIcon className="svg_icons" />{' '}
                                                    </IconButton>
                                                    <Menu {...bindMenu(popupState)}>
                                                        <MenuItem
                                                            className={ETBSG.menuItemEdit}
                                                            onClick={() =>
                                                                handleClick(popupState, 'edit', emailTemplate.sectionId)
                                                            }
                                                        >
                                                            {t('general.edit')}{' '}
                                                        </MenuItem>
                                                        <MenuItem
                                                            className={ETBSG.menuItemDelete}
                                                            onClick={() =>
                                                                handleClick(
                                                                    popupState,
                                                                    'delete',
                                                                    emailTemplate.sectionId
                                                                )
                                                            }
                                                        >
                                                            {t('general.delete')}{' '}
                                                        </MenuItem>
                                                    </Menu>
                                                </React.Fragment>
                                            )}
                                        </PopupState>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <Grid className={ETBSG.paginationWrap}>
                    <Stack spacing={2} style={{ alignItems: 'end' }}>
                        <Pagination
                            count={allRowCount}
                            color="primary"
                            page={currentPage}
                            onChange={handlePageChange}
                        />
                    </Stack>
                </Grid>
            </TableContainer>
            </Box>
        </SCUIContent>
    );
};
